//
/* GS://PROTOCOLO
 * Arquivo: src/components/modals/ReceiveAccountModal.jsx
 * Versão: 1.2 – Fix de UI (Select Visível)
 * Objetivo: Corrigir cor do texto do seletor e ampliar opções.
 */

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { receiveAccount } from '../../services/firestoreService';
import { Loader, X, Check, DollarSign } from 'lucide-react';

const ReceiveAccountModal = ({ account, onClose, onSuccess }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    
    const [receiptDate, setReceiptDate] = useState(new Date().toISOString().split('T')[0]);
    const [receivedAmountRaw, setReceivedAmountRaw] = useState(account.amount * 100); 
    const [receivedAmountInput, setReceivedAmountInput] = useState(
        (account.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    );
    const [receiptMethod, setReceiptMethod] = useState('Pix');

    const handleAmountChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); 
        let cents = parseInt(value, 10);
        if (isNaN(cents)) cents = 0;
        const paddedCents = String(cents).padStart(3, '0'); 
        const integerPart = paddedCents.slice(0, -2);
        const decimalPart = paddedCents.slice(-2);
        setReceivedAmountInput(`${parseInt(integerPart, 10).toLocaleString('pt-BR')},${decimalPart}`);
        setReceivedAmountRaw(cents);
    };

    const handleConfirmReceipt = async (e) => {
        e.preventDefault();
        
        const finalAmount = receivedAmountRaw / 100;
        if (finalAmount <= 0) {
            toast.warning("O valor recebido deve ser maior que zero.");
            return;
        }

        setIsProcessing(true);
        try {
            await receiveAccount({
                accountId: account.id,
                originalAccount: account,
                receiptData: {
                    date: receiptDate,
                    amount: finalAmount,
                    method: receiptMethod
                }
            });
            
            toast.success(`Recebimento confirmado!`);
            onSuccess();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao processar recebimento.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                
                {/* Header (Azul para Recebimento) */}
                <div className="bg-blue-900 p-4 flex justify-between items-center border-b border-blue-800">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2">
                        <DollarSign className="text-green-400" /> Confirmar Recebimento
                    </h3>
                    <button onClick={onClose} className="text-blue-200 hover:text-white transition">
                        <X size={24} />
                    </button>
                </div>

                <div className="bg-gray-50 p-4 border-b border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-bold">Recebendo de:</p>
                    <p className="text-gray-800 font-semibold text-lg truncate">{account.description}</p>
                </div>

                <form onSubmit={handleConfirmReceipt} className="p-6 space-y-4">
                    
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Data do Recebimento</label>
                        <input 
                            type="date"
                            required
                            value={receiptDate}
                            onChange={(e) => setReceiptDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 bg-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Valor Recebido (R$)</label>
                        <input 
                            type="text"
                            required
                            value={receivedAmountInput}
                            onChange={handleAmountChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none font-mono text-lg font-bold text-green-700 bg-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Forma de Recebimento</label>
                        <select 
                            value={receiptMethod}
                            onChange={(e) => setReceiptMethod(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-700"
                        >
                            <option value="Pix">Pix</option>
                            <option value="Dinheiro">Dinheiro (Espécie)</option>
                            <option value="Cartão de Crédito">Cartão de Crédito</option>
                            <option value="Cartão de Débito">Cartão de Débito</option>
                            <option value="Transferência Bancária">Transferência Bancária</option>
                        </select>
                    </div>

                    <div className="flex gap-3 mt-6 pt-2">
                        <button 
                            type="button" 
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition"
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            disabled={isProcessing}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold shadow-md transition flex items-center justify-center gap-2"
                        >
                            {isProcessing ? <Loader className="animate-spin" /> : <Check />}
                            Confirmar
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default ReceiveAccountModal;